import {
  type TRelease,
  type TReleaseHighlight,
  selectReleases,
} from '@qmulus/ui/components/release-highlights/release-highlights.ts';
import type {
  TChangelogRelease,
} from '@qmulus/ui/utils/changelog.ts';
import type {
  TIsoDate,
} from '@qmulus/ui/utils/date.ts';
import {
  compareVersions,
} from '@qmulus/ui/utils/version.ts';

import {
  RELEASE_2_5_0,
} from './releases/2_5_0.ts';

import type {
  TSupportedLocale,
} from '@/types/i18n.ts';

/** The folder `public/release-highlights`, which holds one folder of media per version. */
export const RELEASE_HIGHLIGHTS_MEDIA_BASE_URL = '/release-highlights';

/**
 * The highlights of one release in every locale. Each locale has its own list, so that images and
 * videos can show the application in the language of the text.
 */
export type TReleaseHighlightsDefinition = {
  version: string,
  /** Only needed when the changelog has no date for the version. */
  releaseDate?: TIsoDate,
  highlights: Record<TSupportedLocale, TReleaseHighlight[]>,
};

function getReleaseNotes(changelogRelease: TChangelogRelease | undefined): string | undefined {
  const releaseNotes = changelogRelease?.releaseNotes;
  return releaseNotes === '' ? undefined : releaseNotes;
}

/**
 * The releases with highlights in the locale, followed by the releases of the changelog without
 * highlights. The release notes and, unless the definition sets one, the release date come from
 * the section of the version in the changelog, which is only written in English.
 */
export function getReleasesInLocale({
  definitions,
  locale,
  changelogReleases,
}: {
  definitions: TReleaseHighlightsDefinition[],
  locale: TSupportedLocale,
  changelogReleases: TChangelogRelease[],
}): TRelease[] {
  const releasesWithHighlights = definitions.map((definition): TRelease => {
    const changelogRelease = changelogReleases
      .find((release) => compareVersions(release.version, definition.version) === 0);
    return {
      version: definition.version,
      releaseDate: definition.releaseDate ?? changelogRelease?.releaseDate,
      highlights: definition.highlights[locale],
      releaseNotes: getReleaseNotes(changelogRelease),
    };
  });

  // Some versions have several sections in the changelog; like the release notes of a release with
  // highlights, only the first one is used.
  const firstChangelogReleases = changelogReleases.filter((changelogRelease, index) => changelogReleases
    .findIndex((release) => compareVersions(release.version, changelogRelease.version) === 0) === index);

  const releasesWithoutHighlights = firstChangelogReleases
    .filter((changelogRelease) => !definitions
      .some((definition) => compareVersions(definition.version, changelogRelease.version) === 0))
    .map((changelogRelease): TRelease => ({
      version: changelogRelease.version,
      releaseDate: changelogRelease.releaseDate,
      highlights: [],
      releaseNotes: getReleaseNotes(changelogRelease),
    }));

  return [
    ...releasesWithHighlights,
    ...releasesWithoutHighlights,
  ];
}

/**
 * Release highlights are introduced with this version, so no user has a last known version before
 * it. Until a later release has highlights, users without one see the highlights of this version
 * instead of being treated as new users, as existing users would otherwise never see them.
 */
const RELEASE_HIGHLIGHTS_INTRODUCTION_VERSION = RELEASE_2_5_0.version;

function isReleaseHighlightsIntroduction({
  releases,
  currentVersion,
}: {
  releases: TRelease[],
  currentVersion: string,
}): boolean {
  const releasesUpToCurrentVersion = selectReleases({
    releases,
    currentVersion,
  });
  return releasesUpToCurrentVersion.length === 1
    && releasesUpToCurrentVersion[0].version === RELEASE_HIGHLIGHTS_INTRODUCTION_VERSION;
}

export type TReleaseHighlightsStartupAction = 'storeCurrentVersion' | 'showModal' | 'none';

/**
 * What to do once the user settings are available after signing in. New users have no last known
 * version; for them the current version is stored, so that they only see the releases after it.
 * While release highlights are being introduced, they are shown the highlights of the introduction
 * version instead.
 */
export function getReleaseHighlightsStartupAction({
  releases,
  currentVersion,
  lastKnownVersion,
  hasOptedOut,
}: {
  releases: TRelease[],
  currentVersion: string,
  lastKnownVersion: string | undefined,
  hasOptedOut: boolean,
}): TReleaseHighlightsStartupAction {
  if (hasOptedOut) {
    return lastKnownVersion === undefined ? 'storeCurrentVersion' : 'none';
  }
  if (lastKnownVersion === undefined) {
    return isReleaseHighlightsIntroduction({
      releases,
      currentVersion,
    })
      ? 'showModal'
      : 'storeCurrentVersion';
  }
  const unseenReleases = selectReleases({
    releases,
    currentVersion,
    lastKnownVersion,
  });
  return unseenReleases.length > 0 ? 'showModal' : 'none';
}
